import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
	Alert,
	AlertColor,
	Backdrop,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';
import {
	IBancardFormData,
	IPaymentTitularBancard,
	ITransactionInGatewayBancardResponse,
	PaymentTitularFormFieldLength,
} from '../../types';
import { checkShowErrorMessage } from '../../../common/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import makeStyles from '@mui/styles/makeStyles';
import { gatewayDistinctToDLocalDocumentTypeList } from 'features/transactions/types';
import { cancelBancardTransaction, createBancardTransaction } from 'features/payment/services';
import { FormFieldsMaxLength } from 'features/common/types';
import { emailPattern, requiredWithMessage } from 'features/common/constants';
import { BancardFormModal } from '../BancardFormModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
	helperTextDanger: {
		color: 'red',
	},
}));

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

export const BancardClientTransaction = (): JSX.Element | null => {
	const { payment } = useSelector((state: RootState) => state);

	if (payment.orderDataToClient == null) {
		return null;
	}

	const defaultTransactionValues = {
		orderId: payment.orderDataToClient.orderId,
		firstName: '',
		lastName: '',
		document: '',
		documentType: '',
		email: '',
		phone: '',
	};
	const classes = useStyles();
	const {
		control,
		formState: { errors, isValid },
		handleSubmit,
		reset,
	} = useForm({
		mode: 'all',
		defaultValues: { ...defaultTransactionValues, orderId: payment.orderDataToClient.orderId },
	});
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = React.useState<ShowAlertState>(defaultAlertState);
	const [transactionResponse, setTransactionResponse] = useState<ITransactionInGatewayBancardResponse | null>(null);

	const handleCancelBancardTransaction = async () => {
		try {
			setLoading(true);
			transactionResponse && (await cancelBancardTransaction(transactionResponse.shop_process_id));
			setTransactionResponse(null);
			setAlert({
				show: true,
				severity: 'success',
				message: 'Se ha cancelado con éxito la transacción',
			});
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al cancelar la transacción'),
			});
			setLoading(false);
		}
	};

	const onSubmit = async (data: any) => {
		try {
			setLoading(true);
			const titularInfoFromForm: IPaymentTitularBancard = (({
				firstName,
				lastName,
				email,
				phone,
				documentType,
				document,
			}) => ({ firstName, lastName, email, phone, documentType, document }))(data);
			titularInfoFromForm.email == '' && delete titularInfoFromForm.email;
			titularInfoFromForm.phone == '' && delete titularInfoFromForm.phone;
			const dataToSend: IBancardFormData = {
				paymentTitularBancard: titularInfoFromForm,

				orderId: payment.orderDataToClient?.orderId || '',
			};

			const bancardFormResponse = (await createBancardTransaction(dataToSend)).data;
			if (bancardFormResponse.status == 'success') {
				setTransactionResponse(bancardFormResponse);
			} else {
				setAlert({
					show: true,
					severity: 'error',
					message: 'No se pudo iniciar la transacción con la pasarela de pago',
				});
			}
			setLoading(false);
			reset();
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al iniciar la transacción con la pasarela de pago'),
			});
			setLoading(false);
		}
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Typography variant="h6">Orden de pago por:</Typography>
			<Typography variant="h6">
				{payment.orderDataToClient?.coinCode} {payment.orderDataToClient?.amount}
			</Typography>
			<Typography variant="h6" paddingBottom={2}>
				Datos del Titular de Pago
			</Typography>
			<form noValidate onSubmit={handleSubmit(onSubmit)}>
				<Grid container spacing={2} justifyContent="center">
					<Grid item xs={12} md={6}>
						<Controller
							name="firstName"
							control={control}
							rules={{
								required: requiredWithMessage,
								maxLength: {
									value: PaymentTitularFormFieldLength.FIRSTNAME,
									message:
										'El nombre excede la cantidad de caracteres permitida de ' +
										PaymentTitularFormFieldLength.FIRSTNAME,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Nombre"
									helperText={checkShowErrorMessage(Boolean(errors.firstName), errors.firstName?.message)}
									error={Boolean(errors.firstName)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name="lastName"
							control={control}
							rules={{
								required: requiredWithMessage,
								maxLength: {
									value: PaymentTitularFormFieldLength.LASTNAME,
									message:
										'El apellido excede la cantidad de caracteres permitida de ' +
										PaymentTitularFormFieldLength.LASTNAME,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Apellido"
									helperText={checkShowErrorMessage(Boolean(errors.lastName), errors.lastName?.message)}
									error={Boolean(errors.lastName)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name="email"
							control={control}
							rules={{
								pattern: {
									value: emailPattern,
									message: 'Ingrese un email válido',
								},
								maxLength: {
									value: FormFieldsMaxLength.EMAIL,
									message: 'El email excede el largo máximo permitido de: ' + FormFieldsMaxLength.EMAIL + ' caracteres',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									fullWidth
									label="E-Mail"
									helperText={checkShowErrorMessage(Boolean(errors.email), errors.email?.message)}
									error={Boolean(errors.email)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name="phone"
							control={control}
							rules={{
								maxLength: {
									value: FormFieldsMaxLength.PHONE,
									message: 'El teléfono excede la cantidad de caracteres permitida de ' + FormFieldsMaxLength.PHONE,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									fullWidth
									type="tel"
									label="Teléfono"
									helperText={checkShowErrorMessage(Boolean(errors.phone), errors.phone?.message)}
									error={Boolean(errors.phone)}
									{...field}
								/>
							)}
						/>
					</Grid>

					<Grid item xs={6} md={6}>
						<Controller
							name="documentType"
							control={control}
							rules={{
								required: requiredWithMessage,
							}}
							render={({ field }) => (
								<FormControl sx={{ width: '100%' }}>
									<InputLabel id="document-type-label">Tipo de Documento</InputLabel>
									<Select label="Tipo de Documento" {...field}>
										{gatewayDistinctToDLocalDocumentTypeList?.map((document) => {
											return (
												<MenuItem key={document.id} value={document.id}>
													{document.name}
												</MenuItem>
											);
										})}
									</Select>
								</FormControl>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name="document"
							control={control}
							rules={{
								required: requiredWithMessage,
								maxLength: {
									value: PaymentTitularFormFieldLength.DOCUMENT,
									message:
										'El documento excede la cantidad de caracteres permitida de ' +
										PaymentTitularFormFieldLength.DOCUMENT,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Documento de identidad"
									helperText={checkShowErrorMessage(Boolean(errors.document), errors.document?.message)}
									error={Boolean(errors.document)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Button
							variant="contained"
							sx={{ height: '100%', width: '100%' }}
							type="submit"
							disabled={!isValid}
							color="success"
						>
							Procesar
						</Button>
					</Grid>
				</Grid>
			</form>
			{transactionResponse && (
				<BancardFormModal
					open={transactionResponse !== null}
					processId={transactionResponse.process_id}
					onCancel={handleCancelBancardTransaction}
				/>
			)}
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
